import { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Plane, Upload, List as ListIcon, Settings as SettingsIcon, Wifi, WifiOff, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { subscribe, flushQueue } from '../../lib/flightSyncApi';
import { listPending, addFlight, pruneOldSynced } from '../../lib/flightSyncDb';
import { getActiveProfile, addProfile, switchProfile, resetAll, restoreFromIdbAsync } from '../../lib/flightSyncProfiles';
import { ensurePermission } from '../../lib/flightSyncPush';
import { scanForNewFiles, getWatchHandle, watchFolderSupported } from '../../lib/flightSyncWatch';
import { parseDjiLog } from '../../lib/djiLogParser';
import OnboardingPage from './OnboardingPage';
import ImportPage from './ImportPage';
import FlightsLocalPage from './FlightsLocalPage';
import FlightDetailPage from './FlightDetailPage';
import SettingsPage from './SettingsPage';
import HomePage from './HomePage';
import SharePage from './SharePage';
import HelpPage from './HelpPage';
import './FlightSync.css';

const SCAN_INTERVAL_MS = 30_000; // 30 seconds

const TABS = [
  { path: '/flightsync', icon: Plane, label: 'INICIO' },
  { path: '/flightsync/import', icon: Upload, label: 'IMPORTAR' },
  { path: '/flightsync/flights', icon: ListIcon, label: 'VOOS' },
  { path: '/flightsync/settings', icon: SettingsIcon, label: 'AJUSTES' },
];

export default function FlightSyncApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [ctx, setCtx] = useState(() => getActiveProfile());
  const [restoring, setRestoring] = useState(!getActiveProfile());
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Ask for notification permission on first load after setup
  useEffect(() => {
    if (ctx?.token) ensurePermission().catch(() => {});
  }, [ctx]);

  // Restore profile from IndexedDB backup if localStorage got cleared on reboot
  useEffect(() => {
    if (!ctx) {
      restoreFromIdbAsync().then((restored) => {
        if (restored) setCtx(restored);
        setRestoring(false);
      });
    } else {
      setRestoring(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshPending = useCallback(async () => {
    const items = await listPending();
    setPendingCount(items.length);
  }, []);

  // ==== Auto-scan of watched folder (File System Access API) ====
  const scanRef = useRef(false);
  const ctxToken = ctx?.token;
  const runScan = useCallback(async () => {
    if (scanRef.current || !ctxToken) return;
    if (!watchFolderSupported()) return;
    const handle = await getWatchHandle();
    if (!handle) return;
    scanRef.current = true;
    try {
      const files = await scanForNewFiles();
      if (files.length === 0) return;
      toast.info(`Detectados ${files.length} novo(s) log(s) na pasta monitorada`);
      for (const f of files) {
        try {
          const parsed = await parseDjiLog(f);
          const flight = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            ...parsed,
            pilot_name: ctx.pilot_name,
            drone_model: parsed.drone_model || ctx.drone_model,
            drone_serial: parsed.drone_serial || ctx.drone_serial,
            synced: false,
            created_at: new Date().toISOString(),
            notes: `[auto-scan] ${f.name}`,
          };
          await addFlight(flight);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[auto-scan] Falha ao parsear', f.name, e);
        }
      }
      await pruneOldSynced();
      refreshPending();
      if (navigator.onLine) flushQueue(ctxToken);
    } finally {
      scanRef.current = false;
    }
  }, [ctx, ctxToken, refreshPending]);

  // Scanner: run once on setup, then every SCAN_INTERVAL_MS
  useEffect(() => {
    if (!ctxToken) return;
    runScan();
    const it = setInterval(runScan, SCAN_INTERVAL_MS);
    const onFocus = () => runScan();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(it); window.removeEventListener('focus', onFocus); };
  }, [ctxToken, runScan]);

  useEffect(() => {
    refreshPending();
    const interval = setInterval(refreshPending, 5000);
    return () => clearInterval(interval);
  }, [refreshPending]);

  // Online/offline listener
  useEffect(() => {
    const onOn = () => { setOnline(true); if (ctx?.token) flushQueue(ctx.token); };
    const onOff = () => setOnline(false);
    window.addEventListener('online', onOn);
    window.addEventListener('offline', onOff);
    return () => {
      window.removeEventListener('online', onOn);
      window.removeEventListener('offline', onOff);
    };
  }, [ctx]);

  // Subscribe to sync events
  useEffect(() => {
    const unsub = subscribe((evt) => {
      if (evt.type === 'flush:start') setSyncing(true);
      if (evt.type === 'flush:end') {
        setSyncing(false);
        refreshPending();
      }
    });
    return unsub;
  }, [refreshPending]);

  // Persist context on change
  useEffect(() => {
    if (ctx?.id) switchProfile(ctx.id);
  }, [ctx]);

  const handleOnboarded = (newCtx) => {
    const profile = addProfile(newCtx);
    setCtx(profile);
    navigate('/flightsync/import');
  };

  const handleReset = () => {
    resetAll();
    setCtx(null);
    navigate('/flightsync');
  };

  const hasSetup = Boolean(ctx?.token && ctx?.project_id);

  return (
    <div className="fs-root">
      <Toaster position="top-center" toastOptions={{ style: { background: '#0b0e1a', color: '#e6f7ff', border: '1px solid rgba(0,212,255,0.3)' } }} />

      {/* Header */}
      <header className="fs-header" data-testid="flightsync-header">
        <div className="fs-logo">
          <div className="fs-logo-badge"><Plane size={18} /></div>
          <span>FlightSync</span>
        </div>
        <div className="fs-status">
          {syncing ? (
            <>
              <Loader2 size={14} className="fs-spin" style={{ color: 'var(--fs-cyan)' }} />
              <span style={{ color: 'var(--fs-cyan)' }}>SINCRONIZANDO...</span>
            </>
          ) : online ? (
            <>
              <span className="fs-status-dot" />
              <Wifi size={13} />
              <span>ONLINE</span>
              {pendingCount > 0 && <span className="fs-pill fs-pill-amber" style={{ marginLeft: 6 }}>{pendingCount} PENDENTE{pendingCount > 1 ? 'S' : ''}</span>}
            </>
          ) : (
            <>
              <span className="fs-status-dot offline" />
              <WifiOff size={13} />
              <span>OFFLINE</span>
              {pendingCount > 0 && <span className="fs-pill fs-pill-amber" style={{ marginLeft: 6 }}>{pendingCount}</span>}
            </>
          )}
        </div>
      </header>

      <main className="fs-container">
        {restoring ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--fs-text-dim)' }} data-testid="fs-restoring">
            <Loader2 size={32} className="fs-spin" style={{ color: 'var(--fs-cyan)', marginBottom: 12 }} />
            <p style={{ fontSize: 13 }}>Restaurando sessao...</p>
          </div>
        ) : (
          <Routes>
            <Route index element={hasSetup ? <HomePage ctx={ctx} onGoImport={() => navigate('/flightsync/import')} /> : <OnboardingPage onDone={handleOnboarded} />} />
            <Route path="setup" element={<OnboardingPage onDone={handleOnboarded} />} />
            <Route path="import" element={hasSetup ? <ImportPage ctx={ctx} /> : <Navigate to="/flightsync" replace />} />
            <Route path="flights" element={hasSetup ? <FlightsLocalPage ctx={ctx} /> : <Navigate to="/flightsync" replace />} />
            <Route path="flights/:id" element={hasSetup ? <FlightDetailPage ctx={ctx} /> : <Navigate to="/flightsync" replace />} />
            <Route path="share" element={hasSetup ? <SharePage ctx={ctx} /> : <Navigate to="/flightsync" replace />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="settings" element={<SettingsPage ctx={ctx} onReset={handleReset} onChange={setCtx} />} />
            <Route path="*" element={<Navigate to="/flightsync" replace />} />
          </Routes>
        )}
      </main>

      {/* Bottom tab bar */}
      {hasSetup && !restoring && (
        <nav className="fs-tabbar" data-testid="flightsync-tabbar">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = location.pathname === t.path;
            return (
              <button
                key={t.path}
                className={`fs-tab${active ? ' active' : ''}`}
                onClick={() => navigate(t.path)}
                data-testid={`fs-tab-${t.label.toLowerCase()}`}
              >
                <Icon size={20} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}
